import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useILScore } from '@/hooks/useILScore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Flame, TrendingDown, TrendingUp, Loader2, AlertCircle, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useEffect } from 'react';

export function InflammationDashboard() {
  const { data, isLoading, todayScore, sevenDayAvg, thirtyDayAvg, computeScore, isComputing } = useILScore(90);
  const navigate = useNavigate();
  const { trackFeatureUsage } = useAnalytics();

  useEffect(() => {
    if (todayScore) {
      trackFeatureUsage('inflammation_dashboard', 'viewed', { 
        score: todayScore.il_score, 
        has_hrv: todayScore.hrv_score !== null 
      });
    }
  }, [todayScore]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!todayScore && !isLoading) {
    return (
      <Card className="p-8 text-center">
        <Flame className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">No IL Data Yet</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Start logging your food, sleep, stress, and activity to see your Inflammatory Load score
        </p>
        <Button onClick={() => computeScore(undefined)} disabled={isComputing}>
          {isComputing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Compute My Score
        </Button>
      </Card>
    );
  }

  const bucket = todayScore!.il_score < 34 ? 'low' : todayScore!.il_score < 67 ? 'moderate' : 'high';
  const bucketConfig = {
    low: { 
      label: 'Low', 
      color: 'text-primary',
      bg: 'bg-primary/10',
      description: 'Your lifestyle patterns support healthy inflammation levels. Keep up the great work!'
    },
    moderate: { 
      label: 'Moderate', 
      color: 'text-accent',
      bg: 'bg-accent/10',
      description: 'Some lifestyle factors may be contributing to inflammation. Small changes can make a big difference.'
    },
    high: { 
      label: 'High', 
      color: 'text-destructive',
      bg: 'bg-destructive/10',
      description: 'Multiple lifestyle factors are contributing to inflammation. Let\'s work together to improve this.'
    }
  };

  const change7d = sevenDayAvg ? todayScore!.il_score - sevenDayAvg : 0;
  const change30d = thirtyDayAvg ? todayScore!.il_score - thirtyDayAvg : 0;

  const chartData = data?.slice().reverse().map(d => ({
    date: format(new Date(d.date), 'MMM dd'),
    score: Math.round(d.il_score)
  })) || [];

  const hasHRV = todayScore!.hrv_score !== null;
  const contributors = [
    { key: 'diet', label: 'Diet (UPF/Sugar)', score: todayScore!.diet_score },
    { key: 'smoking', label: 'Smoking', score: todayScore!.smoking_score },
    { key: 'sleep', label: 'Sleep', score: todayScore!.sleep_score },
    { key: 'activity', label: 'Activity', score: todayScore!.activity_score },
    { key: 'stress', label: 'Stress', score: todayScore!.stress_score },
    { key: 'alcohol', label: 'Alcohol', score: todayScore!.alcohol_score },
    { key: 'hydration', label: 'Hydration', score: todayScore!.hydration_score },
    { key: 'social', label: 'Social Connection', score: todayScore!.social_connection_score },
    ...(hasHRV ? [{ key: 'hrv', label: 'HRV', score: todayScore!.hrv_score }] : [])
  ].sort((a, b) => b.score - a.score);

  return (
    <div className="space-y-6">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="text-sm">
          <strong>Educational Score:</strong> This is based on lifestyle data, not a medical test. 
          If symptoms persist, speak to your GP.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Your Score Today</h3>
            <Badge className={bucketConfig[bucket].bg}>
              {bucketConfig[bucket].label}
            </Badge>
          </div>

          <motion.div
            className={`text-6xl font-bold ${bucketConfig[bucket].color} mb-4`}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, type: 'spring' }}
          >
            {Math.round(todayScore!.il_score)}
          </motion.div>

          <p className="text-sm text-muted-foreground mb-6">
            {bucketConfig[bucket].description}
          </p>

          <div className="space-y-3">
            {sevenDayAvg && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">7-day change</span>
                <div className="flex items-center gap-1">
                  {change7d > 0 ? (
                    <TrendingUp className="w-4 h-4 text-destructive" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-primary" />
                  )}
                  <span className={change7d > 0 ? 'text-destructive' : 'text-primary'}>
                    {change7d > 0 ? '+' : ''}{change7d.toFixed(1)}
                  </span>
                </div>
              </div>
            )}
            {thirtyDayAvg && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">30-day change</span>
                <div className="flex items-center gap-1">
                  {change30d > 0 ? (
                    <TrendingUp className="w-4 h-4 text-destructive" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-primary" />
                  )}
                  <span className={change30d > 0 ? 'text-destructive' : 'text-primary'}>
                    {change30d > 0 ? '+' : ''}{change30d.toFixed(1)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <Button
            onClick={() => computeScore(undefined)}
            disabled={isComputing}
            variant="outline"
            className="w-full mt-4"
          >
            {isComputing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Refresh Score
          </Button>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Top Contributors</h3>
          <div className="space-y-4">
            {contributors.slice(0, 3).map((contributor, index) => {
              const explanation = {
                diet: 'Processing level, sugar intake, fibre, and omega-3 consumption',
                smoking: 'Cigarettes per day and tobacco/vape usage',
                sleep: 'Duration, quality, and consistency patterns',
                activity: 'Weekly moderate/vigorous minutes and overtraining balance',
                stress: 'Self-reported levels and recovery balance',
                alcohol: 'Weekly units and binge episodes',
                hydration: 'Daily water intake and cellular function',
                social: 'Meaningful interactions and loneliness levels',
                hrv: 'Heart rate variability vs your baseline'
              }[contributor.key];

              const tip = {
                diet: 'Focus on whole foods, reduce ultra-processed items, and boost omega-3s',
                smoking: 'Smoking dramatically increases inflammation. NHS Stop Smoking Service: 0300 123 1044',
                sleep: 'Maintain consistent bed/wake times within ±30 minutes',
                activity: 'Aim for 150-300 minutes weekly. Avoid overtraining without recovery',
                stress: '2× 5-minute breathing sessions daily can help regulate your nervous system',
                alcohol: 'Keep under 14 units weekly and avoid binge episodes (6+ units)',
                hydration: 'Target 2-3L water daily. Front-load intake before 7pm',
                social: 'Daily meaningful interaction. Phone calls, in-person meetings, group activities',
                hrv: 'Prioritise recovery days when HRV drops below baseline'
              }[contributor.key];

              return (
                <div key={contributor.key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium capitalize">{contributor.label}</p>
                      <p className="text-xs text-muted-foreground">{explanation}</p>
                    </div>
                    <Badge variant="outline">
                      {Math.round(contributor.score * 100)}%
                    </Badge>
                  </div>
                  <Progress value={contributor.score * 100} className="h-2" />
                  <p className="text-xs text-muted-foreground italic">{tip}</p>
                </div>
              );
            })}
          </div>

          <Button
            onClick={() => {
              trackFeatureUsage('inflammation_dashboard', 'therappi_clicked', { score: todayScore!.il_score });
              navigate('/therapy');
            }}
            variant="default"
            className="w-full mt-6"
          >
            Ask Therappi for Guidance
          </Button>

          {!hasHRV && (
            <Alert className="mt-4" role="status" aria-live="polite">
              <Activity className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Connect WHOOP for HRV tracking:</strong> Add heart rate variability data to get even more personalised inflammation insights.
                <Button 
                  variant="link" 
                  className="p-0 h-auto ml-1"
                  onClick={() => {
                    trackFeatureUsage('inflammation_dashboard', 'whoop_connect_clicked');
                    navigate('/settings?tab=integrations');
                  }}
                >
                  Connect now →
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">90-Day Trend</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                className="text-muted-foreground"
              />
              <YAxis 
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                className="text-muted-foreground"
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-sm text-center text-foreground/80 mt-4 font-light">
          Lower scores indicate less inflammatory lifestyle patterns. Aim for consistent improvement over time.
        </p>
      </Card>
    </div>
  );
}
